/*
 * API service module.
 *
 * This module centralizes all HTTP requests made by the frontend to
 * the backend.  Keeping network logic in one place makes the
 * components easier to test because we can mock these functions
 * instead of stubbing out fetch globally.  Functions return JSON
 * responses or throw when the response is not ok.
 */

import { clearStoredLastVisitedRoute } from "./lastVisitedRoute.js";

const BASE_URL = '/api';
const REMEMBER_ME_STORAGE_KEY = 'wishlist_remember_me';
const AUTH_NOT_AUTHENTICATED = 'auth_not_authenticated';
const AUTH_ACCESS_TOKEN_INVALID = 'auth_access_token_invalid';
const AUTH_ACCESS_TOKEN_EXPIRED = 'auth_access_token_expired';
const AUTH_USER_INACTIVE = 'auth_user_inactive';
const AUTH_SESSION_INVALID = 'auth_session_invalid';

let accessToken = null;
let refreshRequestPromise = null;

/**
 * Normalize an API error payload and attach the HTTP status code.
 *
 * @param {any} payload Parsed response body
 * @param {number} statusCode HTTP status code
 * @returns {Object}
 */
function buildApiError(payload, statusCode) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return {
      ...payload,
      status: statusCode,
    };
  }
  return {
    detail: typeof payload === 'string' && payload ? payload : 'Request failed.',
    status: statusCode,
  };
}

/**
 * Persist the remember me choice so the app can restore the session policy.
 *
 * @param {boolean} remember Whether the session should persist
 */
function setRememberMePreference(remember) {
  window.localStorage.setItem(REMEMBER_ME_STORAGE_KEY, remember ? 'true' : 'false');
}

/**
 * Read the stored remember me preference.
 *
 * @returns {boolean}
 */
function getRememberMePreference() {
  return window.localStorage.getItem(REMEMBER_ME_STORAGE_KEY) !== 'false';
}

/**
 * Retrieve the in-memory access token.
 *
 * @returns {string|null}
 */
function getAccessToken() {
  return accessToken;
}

/**
 * Persist an access token in memory and keep the remember me choice separately.
 *
 * @param {string} token The JWT access token to persist
 * @param {boolean} remember Whether to persist across browser sessions
 */
function setAccessToken(token, remember) {
  accessToken = token;
  setRememberMePreference(remember);
}

/**
 * Clear the access token while preserving the remember me preference.
 */
function clearAccessToken() {
  accessToken = null;
}

/**
 * Read the CSRF token from browser cookies when available.
 *
 * @returns {string|null}
 */
function readCsrfTokenFromCookies() {
  try {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : null;
  } catch (error) {
    return null;
  }
}

/**
 * Return true when the API response indicates an authentication issue.
 *
 * @param {number} statusCode HTTP status code
 * @param {Object|string} payload Parsed response payload
 * @returns {boolean}
 */
function isAuthenticationFailure(statusCode, payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false;
  }
  if (statusCode !== 401 && statusCode !== 403) {
    return false;
  }
  return [
    AUTH_NOT_AUTHENTICATED,
    AUTH_ACCESS_TOKEN_INVALID,
    AUTH_ACCESS_TOKEN_EXPIRED,
    AUTH_USER_INACTIVE,
  ].includes(payload.code);
}

/**
 * Return true when the backend explicitly says the session is no longer valid.
 *
 * @param {unknown} errorPayload API error payload
 * @returns {boolean}
 */
function isSessionInvalidError(errorPayload) {
  return Boolean(
    errorPayload &&
    typeof errorPayload === 'object' &&
    !Array.isArray(errorPayload) &&
    errorPayload.code === AUTH_SESSION_INVALID
  );
}

/**
 * Reuse a single refresh request while multiple API calls wait for a renewed token.
 *
 * @returns {Promise<string>} fresh access token
 */
async function ensureFreshAccessToken() {
  if (!refreshRequestPromise) {
    refreshRequestPromise = refreshAccessToken()
      .then((refreshData) => {
        if (!refreshData || !refreshData.access_token) {
          throw new Error('Refresh response did not include an access token.');
        }
        setAccessToken(refreshData.access_token, getRememberMePreference());
        return refreshData.access_token;
      })
      .catch((refreshError) => {
        if (isSessionInvalidError(refreshError)) {
          clearAccessToken();
        }
        throw refreshError;
      })
      .finally(() => {
        refreshRequestPromise = null;
      });
  }

  return refreshRequestPromise;
}

/**
 * Attempt to restore an in-memory access token from the refresh cookie.
 *
 * @returns {Promise<string|null>} restored access token or null
 */
async function restoreAccessSession() {
  try {
    return await ensureFreshAccessToken();
  } catch (error) {
    if (isSessionInvalidError(error)) {
      return null;
    }
    throw error;
  }
}

async function jsonRequest(path, options = {}) {
  const shouldSendAuthHeader = options.skipAuth !== true;

  /**
   * Perform a fetch and parse either JSON or text.
   *
   * @param {string|null} accessToken The access token to attach, or null
   * @returns {Promise<{response: Response, data: any}>}
   */
  async function makeRequest(accessToken) {
    const authHeader =
      shouldSendAuthHeader && accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

    // Determine whether we are sending multipart/form-data.  When the body
    // is a FormData instance the browser will automatically set the
    // appropriate Content‑Type header including the boundary.  In that
    // case omit the JSON content type header entirely.  See
    // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects
    const isMultipart = options.body instanceof FormData;

    const response = await fetch(`${BASE_URL}${path}`, {
      headers: {
        ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
        ...authHeader,
        ...(options.headers || {}),
      },
      // Always include credentials so that refresh cookies are sent.
      credentials: options.credentials || 'include',
      ...options,
    });

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    return { response, data };
  }

  const initialToken = getAccessToken();
  let { response, data } = await makeRequest(initialToken);

  // If explicitly unauthenticated, attempt to refresh once and retry.
  if (shouldSendAuthHeader && isAuthenticationFailure(response.status, data)) {
    try {
      const refreshedAccessToken = await ensureFreshAccessToken();
      ({ response, data } = await makeRequest(refreshedAccessToken));
    } catch (refreshError) {
      throw refreshError;
    }
  }

  if (!response.ok) {
    throw buildApiError(data, response.status);
  }

  return data;
}

/**
 * Retrieve the list of available countries with localized names.
 *
 * @returns {Promise<Array>} an array of country objects
 */
export async function listCountries() {
  const data = await jsonRequest('/countries/', { method: 'GET', skipAuth: true });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Retrieve the list of cities matching a search term.
 *
 * @param {string} searchTerm Search value for the city name
 * @returns {Promise<Array>} an array of city objects
 */
export async function listCities(searchTerm, { country } = {}) {
  const query = new URLSearchParams();
  if (searchTerm) query.set('search', searchTerm);
  if (country) query.set('country', country);
  const queryString = query.toString();
  const data = await jsonRequest(`/cities/${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
    skipAuth: true,
  });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Retrieve the current user's default wish list.
 *
 * @returns {Promise<Object>} the wish list object
 */
export async function getWishList() {
  return jsonRequest('/me/wishlist/', { method: 'GET' });
}

/**
 * Retrieve one paginated page of wishes for the current user.
 *
 * @param {Object} params Query params for pagination
 * @param {number|string} params.page Page number
 * @returns {Promise<Object>} paginated wish list items payload
 */
export async function listWishListItems(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (params.q) query.set('q', params.q);
  const queryString = query.toString();
  return jsonRequest(`/me/wishes/${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
  });
}

/**
 * Upload an image for later use by another endpoint.
 *
 * @param {File} imageFile The file selected by the user
 * @param {string} usage The usage type for this upload
 * @returns {Promise<Object>} image upload payload
 */
export async function uploadImage(imageFile, usage) {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('usage', usage);
  return jsonRequest('/media/images/', {
    method: 'POST',
    body: formData,
  });
}

/**
 * Upload a new avatar for the current user.  The image is uploaded first
 * and then attached to the profile via the standard profile update API.
 * On success the updated profile object is returned.  Errors are thrown
 * directly from the underlying ``jsonRequest`` call.
 *
 * @param {File} avatarFile The file selected by the user
 * @returns {Promise<Object>} the updated profile object
 */
export async function uploadAvatar(avatarFile) {
  const upload = await uploadImage(avatarFile, 'profile_avatar');
  return updateProfile({ avatar_upload_id: upload.id });
}

/**
 * Retrieve the current user's friends.  The ``/me/friends/`` endpoint
 * returns paginated results containing user profiles for each friend.  This
 * helper normalises the response into an array of profile objects.  If
 * pagination is disabled the raw response will already be a list.
 *
 * @returns {Promise<Array>} an array of friend profile objects
 */
export async function fetchFriends() {
  const data = await jsonRequest('/me/friends/', { method: 'GET' });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Retrieve a paginated list of friends with optional filters.
 *
 * @param {Object} params Query params for filtering and pagination
 * @returns {Promise<Object>} paginated friend list
 */
export async function listFriends(params = {}) {
  const query = new URLSearchParams();
  if (params.q) query.set('q', params.q);
  if (params.gender) query.set('gender', params.gender);
  if (params.city) query.set('city', params.city);
  if (params.country) query.set('country', params.country);
  if (params.minAge) query.set('min_age', params.minAge);
  if (params.maxAge) query.set('max_age', params.maxAge);
  if (params.page) query.set('page', params.page);
  const queryString = query.toString();
  return jsonRequest(`/me/friends/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Retrieve friendship requests for the current user.
 *
 * @param {Object} params Query params for listing requests
 * @param {string} params.direction Incoming or outgoing requests
 * @param {number|string} params.page Page number
 * @returns {Promise<Object>} paginated request list
 */
export async function listFriendRequests(params = {}) {
  const query = new URLSearchParams();
  if (params.direction) query.set('direction', params.direction);
  if (params.page) query.set('page', params.page);
  const queryString = query.toString();
  return jsonRequest(`/me/friendship-requests/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Retrieve the current user's notifications with pagination and filters.
 *
 * @param {Object} params Query parameters for listing notifications
 * @param {number|string} params.page Page number for pagination
 * @param {boolean} params.isRead Filter by read status when provided
 * @returns {Promise<Object>} paginated notifications payload
 */
export async function listNotifications(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (typeof params.isRead === 'boolean') {
    query.set('is_read', params.isRead ? 'true' : 'false');
  }
  const queryString = query.toString();
  return jsonRequest(`/me/notifications/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Mark a single notification read state.
 *
 * @param {number|string} notificationId Notification identifier
 * @param {boolean} isRead Whether the notification should be read
 * @returns {Promise<Object>} updated notification payload
 */
export async function updateNotificationReadStatus(notificationId, isRead) {
  return jsonRequest(`/me/notifications/${notificationId}/`, {
    method: 'PATCH',
    body: JSON.stringify({ is_read: isRead }),
  });
}

/**
 * Mark notifications as read in bulk.
 *
 * @param {Array<number|string>|null} ids Optional list of notification ids
 * @returns {Promise<Object>} bulk update response
 */
export async function bulkMarkNotificationsRead(ids = null) {
  return jsonRequest('/me/notifications/mark-read/', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

/**
 * Mark notifications as unread in bulk.
 *
 * @param {Array<number|string>|null} ids Optional list of notification ids
 * @returns {Promise<Object>} bulk update response
 */
export async function bulkMarkNotificationsUnread(ids = null) {
  return jsonRequest('/me/notifications/mark-unread/', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

/**
 * Delete notifications in bulk.
 *
 * @param {Array<number|string>|null} ids Optional list of notification ids
 * @returns {Promise<Object>} bulk delete response
 */
export async function bulkDeleteNotifications(ids = null) {
  return jsonRequest('/me/notifications/delete/', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

/**
 * Record that the current user opened the notifications list.
 *
 * @returns {Promise<Object>} response with last seen timestamp
 */
export async function markNotificationsSeen() {
  return jsonRequest('/me/notifications/seen/', { method: 'POST' });
}

/**
 * Retrieve notification summary data for the current user.
 *
 * @returns {Promise<Object>} summary payload
 */
export async function fetchNotificationSummary() {
  return jsonRequest('/me/notifications/summary/', { method: 'GET' });
}

/**
 * Retrieve notification preferences for the current user.
 *
 * @returns {Promise<Object>} preferences payload
 */
export async function fetchNotificationPreferences() {
  return jsonRequest('/me/notification-preferences/', { method: 'GET' });
}

/**
 * Update notification preferences for the current user.
 *
 * @param {Array<Object>} preferences List of preference updates
 * @returns {Promise<Object>} updated preferences payload
 */
export async function updateNotificationPreferences(preferences) {
  return jsonRequest('/me/notification-preferences/', {
    method: 'PATCH',
    body: JSON.stringify({ preferences }),
  });
}

/**
 * Accept an incoming friendship request.
 *
 * @param {number|string} requestId The friendship request identifier
 * @returns {Promise<Object>} response payload
 */
export async function acceptFriendRequest(requestId) {
  return jsonRequest(`/me/friendship-requests/${requestId}/accept/`, { method: 'POST' });
}

/**
 * Decline an incoming friendship request.
 *
 * @param {number|string} requestId The friendship request identifier
 * @returns {Promise<void>} empty response on success
 */
export async function declineFriendRequest(requestId) {
  return jsonRequest(`/me/friendship-requests/${requestId}/decline/`, { method: 'POST' });
}

/**
 * Cancel an outgoing friendship request.
 *
 * @param {string} username The recipient username
 * @returns {Promise<void>} empty response on success
 */
export async function cancelFriendRequest(username) {
  return jsonRequest(`/me/friends/${username}/`, { method: 'DELETE' });
}

/**
 * Remove a friend connection for the current user.
 *
 * @param {string} username The username to remove
 * @returns {Promise<void>} empty response on success
 */
export async function removeFriend(username) {
  return jsonRequest(`/me/friends/${username}/`, { method: 'DELETE' });
}

/**
 * Block a user from viewing the current user's content.
 *
 * @param {string} username The username to block
 * @returns {Promise<void>} empty response on success
 */
export async function blockUser(username) {
  return jsonRequest(`/me/blocks/${username}/`, { method: 'POST' });
}

/**
 * Remove a block for a user.
 *
 * @param {string} username The username to unblock
 * @returns {Promise<void>} empty response on success
 */
export async function unblockUser(username) {
  return jsonRequest(`/me/blocks/${username}/`, { method: 'DELETE' });
}

/**
 * Retrieve a paginated list of blocked users for the current user.
 *
 * @param {Object} params Query params for pagination
 * @param {number|string} params.page Page number
 * @returns {Promise<Object>} paginated blocked user list
 */
export async function listBlockedUsers(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  const queryString = query.toString();
  return jsonRequest(`/me/blocks/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Retrieve a public profile with visibility rules applied.
 *
 * @param {string} username The username to fetch
 * @returns {Promise<Object>} profile payload
 */
export async function fetchPublicProfile(username) {
  return jsonRequest(`/users/${username}/profile/`, { method: 'GET' });
}

/**
 * Send a friend request to the specified username.
 *
 * @param {string} username The username to connect with
 * @returns {Promise<Object>} friend request payload
 */
export async function sendFriendRequest(username) {
  return jsonRequest(`/me/friends/${username}/`, {
    method: 'POST',
  });
}

/**
 * Create a new event for the current user.
 *
 * @param {Object} payload Event payload
 * @returns {Promise<Object>} created event
 */
export async function createEvent(payload) {
  const body = payload instanceof FormData ? payload : JSON.stringify(payload);
  return jsonRequest('/me/events/', {
    method: 'POST',
    body,
  });
}

/**
 * Retrieve a paginated page of the current user's events.
 *
 * @param {Object} params Query params for pagination and search
 * @param {number|string} params.page Page number
 * @param {string} params.q Search query
 * @param {string} params.section Event section filter
 * @returns {Promise<Object>} paginated events payload
 */
export async function listEventsPaginated(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (params.q) query.set('q', params.q);
  if (params.section) query.set('section', params.section);
  const queryString = query.toString();
  return jsonRequest(`/me/events/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Retrieve the current user's events.
 *
 * This helper intentionally returns only the current page results for
 * callers that only need a lightweight sample.
 *
 * @returns {Promise<Array>} an array of event objects
 */
export async function listEvents() {
  const data = await listEventsPaginated();
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Retrieve events for a specific user with visibility enforcement.
 *
 * @param {string} username The username of the event owner
 * @param {Object} params Query params for pagination and search
 * @param {number|string} params.page Page number
 * @param {string} params.q Search query
 * @returns {Promise<Object>} paginated events data
 */
export async function listUserEvents(username, params = {}) {
  const encodedUsername = encodeURIComponent(username);
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (params.q) query.set('q', params.q);
  const queryString = query.toString();
  return jsonRequest(`/users/${encodedUsername}/events/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Retrieve stories for a specific user with visibility enforcement.
 *
 * @param {string} username The username of the story owner
 * @param {Object} params Query params for pagination and search
 * @param {number|string} params.page Page number
 * @param {string} params.q Search query
 * @returns {Promise<Object>} paginated stories data
 */
export async function listUserStories(username, params = {}) {
  const encodedUsername = encodeURIComponent(username);
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (params.q) query.set('q', params.q);
  const queryString = query.toString();
  return jsonRequest(`/users/${encodedUsername}/stories/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Retrieve a paginated page of stories for the current user.
 *
 * @param {Object} params Query params for pagination and search
 * @param {number|string} params.page Page number
 * @param {string} params.q Search query
 * @returns {Promise<Object>} paginated stories payload
 */
export async function listMyStoriesPaginated(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (params.q) query.set('q', params.q);
  const queryString = query.toString();
  return jsonRequest(`/me/stories/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Retrieve stories for the current user.
 *
 * This helper intentionally returns only the current page results for
 * callers that only need a lightweight sample.
 *
 * @returns {Promise<Array>} an array of story objects
 */
export async function listMyStories() {
  const data = await listMyStoriesPaginated();
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Retrieve a single story for the current user.
 *
 * @param {number|string} storyId The story identifier
 * @returns {Promise<Object>} story data
 */
export async function getStory(storyId) {
  return jsonRequest(`/me/stories/${storyId}/`, { method: 'GET' });
}

/**
 * Retrieve comments for a story.
 *
 * @param {number|string} storyId The story identifier
 * @returns {Promise<Array>} array of comment objects
 */
export async function listStoryComments(storyId) {
  const data = await jsonRequest(`/me/stories/${storyId}/comments/`, { method: 'GET' });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create a comment on a story.
 *
 * @param {number|string} storyId The story identifier
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} created comment data
 */
export async function createStoryComment(storyId, payload) {
  return jsonRequest(`/me/stories/${storyId}/comments/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Update one story comment.
 *
 * @param {number|string} storyId The story identifier
 * @param {number|string} commentId The comment identifier
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} updated comment data
 */
export async function updateStoryComment(storyId, commentId, payload) {
  return jsonRequest(`/me/stories/${storyId}/comments/${commentId}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete one story comment.
 *
 * @param {number|string} storyId The story identifier
 * @param {number|string} commentId The comment identifier
 * @returns {Promise<void>} empty response body
 */
export async function deleteStoryComment(storyId, commentId) {
  return jsonRequest(`/me/stories/${storyId}/comments/${commentId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve reactions for a story.
 *
 * @param {number|string} storyId The story identifier
 * @returns {Promise<Array>} array of reaction objects
 */
export async function listStoryReactions(storyId, options = {}) {
  const queryParams = new URLSearchParams();
  if (options.reactionType) {
    queryParams.set('reaction_type', options.reactionType);
  }
  if (options.page != null) {
    queryParams.set('page', String(options.page));
  }
  const queryString = queryParams.toString();
  const path = queryString ? `/me/stories/${storyId}/reactions/?${queryString}` : `/me/stories/${storyId}/reactions/`;
  const data = await jsonRequest(path, { method: 'GET' });
  if (options.raw) {
    return data;
  }
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create or update a reaction on a story.
 *
 * @param {number|string} storyId The story identifier
 * @param {Object} payload Reaction payload
 * @returns {Promise<Object>} created reaction data
 */
export async function createStoryReaction(storyId, payload) {
  return jsonRequest(`/me/stories/${storyId}/reactions/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete the current user's reaction on a story.
 *
 * @param {number|string} storyId The story id
 * @param {number|string} reactionId The reaction id to remove
 * @returns {Promise<void>} empty response body
 */
export async function deleteStoryReaction(storyId, reactionId) {
  return jsonRequest(`/me/stories/${storyId}/reactions/${reactionId}/`, {
    method: 'DELETE',
  });
}

/**
 * Create a new story for the current user.
 *
 * @param {Object|FormData} payload Story payload
 * @returns {Promise<Object>} created story
 */
export async function createStory(payload) {
  const body = payload instanceof FormData ? payload : JSON.stringify(payload);
  return jsonRequest('/me/stories/', {
    method: 'POST',
    body,
  });
}

/**
 * Update a story for the current user.
 *
 * @param {number|string} storyId The story identifier
 * @param {Object|FormData} payload Story payload
 * @returns {Promise<Object>} updated story
 */
export async function updateStory(storyId, payload) {
  const body = payload instanceof FormData ? payload : JSON.stringify(payload);
  return jsonRequest(`/me/stories/${storyId}/`, {
    method: 'PATCH',
    body,
  });
}

/**
 * Delete a story for the current user.
 *
 * @param {number|string} storyId The story identifier
 * @returns {Promise<void>} empty response on success
 */
export async function deleteStory(storyId) {
  return jsonRequest(`/me/stories/${storyId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve a single story for a specific user.
 *
 * @param {string} username The story owner username
 * @param {number|string} storyId The story identifier
 * @returns {Promise<Object>} story data
 */
export async function getUserStory(username, storyId) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/users/${encodedUsername}/stories/${storyId}/`, { method: 'GET' });
}

/**
 * Search the current user's wish list items.
 *
 * @param {string} query Search query
 * @param {Object} params Query params for pagination
 * @param {number|string} params.page Page number
 * @returns {Promise<Object>} paginated wish list items
 */
export async function searchMyWishItemsPaginated(query, params = {}) {
  const queryParams = new URLSearchParams();
  if (query) queryParams.set('q', query);
  if (params.page) queryParams.set('page', params.page);
  if (params.excludeEventId) queryParams.set('exclude_event_id', params.excludeEventId);
  return jsonRequest(`/me/search/items/?${queryParams.toString()}`, { method: 'GET' });
}

/**
 * Search the current user's wish list items.
 *
 * @param {string} query Search query
 * @returns {Promise<Array>} list of wish list items
 */
export async function searchMyWishItems(query) {
  const data = await searchMyWishItemsPaginated(query);
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Retrieve a single event for a specific user.
 *
 * @param {string} username The event owner username
 * @param {number|string} eventId The event identifier
 * @returns {Promise<Object>} event data
 */
export async function getUserEvent(username, eventId) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/users/${encodedUsername}/events/${eventId}/`, { method: 'GET' });
}

/**
 * Retrieve a single event for the current user.
 *
 * @param {number|string} eventId The event identifier
 * @returns {Promise<Object>} event data
 */
export async function getEvent(eventId) {
  return jsonRequest(`/me/events/${eventId}/`, { method: 'GET' });
}

/**
 * Retrieve a paginated page of wish items linked to one owned event.
 *
 * @param {number|string} eventId The event identifier
 * @param {Object} params Query params for pagination and sorting
 * @returns {Promise<Object>} paginated wish item payload
 */
export async function listEventWishItems(eventId, params = {}) {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', params.page);
  if (params.q) queryParams.set('q', params.q);
  if (params.sort) queryParams.set('sort', params.sort);
  const queryString = queryParams.toString();
  return jsonRequest(`/me/events/${eventId}/wish-items/${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
}

/**
 * Notify one person about one current user event.
 *
 * @param {number|string} eventId The event identifier
 * @param {Object} payload Recipient payload
 * @returns {Promise<Object>} notified profile payload
 */
export async function notifyEventProfile(eventId, payload) {
  return jsonRequest(`/me/events/${eventId}/notify/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Retrieve a paginated page of wish items linked to one public event.
 *
 * @param {string} username The event owner username
 * @param {number|string} eventId The event identifier
 * @param {Object} params Query params for pagination and sorting
 * @returns {Promise<Object>} paginated wish item payload
 */
export async function listUserEventWishItems(username, eventId, params = {}) {
  const encodedUsername = encodeURIComponent(username);
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', params.page);
  if (params.q) queryParams.set('q', params.q);
  if (params.sort) queryParams.set('sort', params.sort);
  const queryString = queryParams.toString();
  return jsonRequest(
    `/users/${encodedUsername}/events/${eventId}/wish-items/${queryString ? `?${queryString}` : ''}`,
    { method: 'GET' }
  );
}

/**
 * Quickly reserve one wish item from a public event.
 *
 * @param {string} username The event owner username
 * @param {number|string} eventId The event identifier
 * @param {number|string} itemId The wish item identifier
 * @returns {Promise<Object>} reservation payload
 */
export async function reserveUserEventWishItem(username, eventId, itemId) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/users/${encodedUsername}/events/${eventId}/wish-items/${itemId}/reserve/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Retrieve the attendance summary for one visible event.
 *
 * @param {string} username Event owner username
 * @param {number|string} eventId Event identifier
 * @param {Object} [options] Attendance query options
 * @param {number} [options.page] Attendance page number
 * @returns {Promise<Object>} attendance payload
 */
export async function getUserEventAttendance(username, eventId, options = {}) {
  const encodedUsername = encodeURIComponent(username);
  const searchParams = new URLSearchParams();
  if (options.page) {
    searchParams.set('page', String(options.page));
  }
  const querySuffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return jsonRequest(`/users/${encodedUsername}/events/${eventId}/attendance/${querySuffix}`, { method: 'GET' });
}

/**
 * Mark the current user as attending one visible event.
 *
 * @param {string} username Event owner username
 * @param {number|string} eventId Event identifier
 * @returns {Promise<Object>} attendance mutation payload
 */
export async function attendUserEvent(username, eventId) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/users/${encodedUsername}/events/${eventId}/attendance/`, { method: 'PUT' });
}

/**
 * Remove the current user from one visible event attendance list.
 *
 * @param {string} username Event owner username
 * @param {number|string} eventId Event identifier
 * @returns {Promise<void>} empty response on success
 */
export async function unattendUserEvent(username, eventId) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/users/${encodedUsername}/events/${eventId}/attendance/`, { method: 'DELETE' });
}

/**
 * Retrieve comments for an event.
 *
 * @param {number|string} eventId The event identifier
 * @returns {Promise<Array>} array of comment objects
 */
export async function listEventComments(eventId) {
  const data = await jsonRequest(`/me/events/${eventId}/comments/`, { method: 'GET' });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create a comment on an event.
 *
 * @param {number|string} eventId The event identifier
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} created comment data
 */
export async function createEventComment(eventId, payload) {
  return jsonRequest(`/me/events/${eventId}/comments/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Update one event comment.
 *
 * @param {number|string} eventId The event identifier
 * @param {number|string} commentId The comment identifier
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} updated comment data
 */
export async function updateEventComment(eventId, commentId, payload) {
  return jsonRequest(`/me/events/${eventId}/comments/${commentId}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete one event comment.
 *
 * @param {number|string} eventId The event identifier
 * @param {number|string} commentId The comment identifier
 * @returns {Promise<void>} empty response body
 */
export async function deleteEventComment(eventId, commentId) {
  return jsonRequest(`/me/events/${eventId}/comments/${commentId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve reactions for an event.
 *
 * @param {number|string} eventId The event identifier
 * @returns {Promise<Array>} array of reaction objects
 */
export async function listEventReactions(eventId, options = {}) {
  const queryParams = new URLSearchParams();
  if (options.reactionType) {
    queryParams.set('reaction_type', options.reactionType);
  }
  if (options.page != null) {
    queryParams.set('page', String(options.page));
  }
  const queryString = queryParams.toString();
  const path = queryString ? `/me/events/${eventId}/reactions/?${queryString}` : `/me/events/${eventId}/reactions/`;
  const data = await jsonRequest(path, { method: 'GET' });
  if (options.raw) {
    return data;
  }
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create or update a reaction on an event.
 *
 * @param {number|string} eventId The event identifier
 * @param {Object} payload Reaction payload
 * @returns {Promise<Object>} created reaction data
 */
export async function createEventReaction(eventId, payload) {
  return jsonRequest(`/me/events/${eventId}/reactions/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete the current user's reaction on an event.
 *
 * @param {number|string} eventId The event id
 * @param {number|string} reactionId The reaction id to remove
 * @returns {Promise<void>} empty response body
 */
export async function deleteEventReaction(eventId, reactionId) {
  return jsonRequest(`/me/events/${eventId}/reactions/${reactionId}/`, {
    method: 'DELETE',
  });
}

/**
 * Update an event for the current user.
 *
 * @param {number|string} eventId The event identifier
 * @param {Object} payload The event payload
 * @returns {Promise<Object>} updated event data
 */
export async function updateEvent(eventId, payload) {
  const body = payload instanceof FormData ? payload : JSON.stringify(payload);
  return jsonRequest(`/me/events/${eventId}/`, {
    method: 'PATCH',
    body,
  });
}

/**
 * List profiles excluded from one owned event.
 *
 * @param {number|string} eventId Event identifier
 * @returns {Promise<Array>} excluded profile list
 */
export async function listEventExcludedProfiles(eventId) {
  const data = await jsonRequest(`/me/events/${eventId}/excluded-profiles/`, { method: 'GET' });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Exclude one profile from one owned event.
 *
 * @param {number|string} eventId Event identifier
 * @param {string} username Username to exclude
 * @returns {Promise<Object>} created profile payload
 */
export async function addEventExcludedProfile(eventId, username) {
  return jsonRequest(`/me/events/${eventId}/excluded-profiles/`, {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
}

/**
 * Remove one profile exclusion from one owned event.
 *
 * @param {number|string} eventId Event identifier
 * @param {string} username Username to remove
 * @returns {Promise<void>} empty response on success
 */
export async function removeEventExcludedProfile(eventId, username) {
  return jsonRequest(`/me/events/${eventId}/excluded-profiles/${encodeURIComponent(username)}/`, {
    method: 'DELETE',
  });
}

/**
 * List organizers of one owned or organized event.
 *
 * @param {number|string} eventId Event identifier
 * @returns {Promise<Array>} organizer list
 */
export async function listEventOrganizers(eventId) {
  const data = await jsonRequest(`/me/events/${eventId}/organizers/`, { method: 'GET' });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Add one organizer to an event.
 *
 * @param {number|string} eventId Event identifier
 * @param {Object} payload Organizer payload
 * @returns {Promise<Object>} organizer payload
 */
export async function addEventOrganizer(eventId, payload) {
  return jsonRequest(`/me/events/${eventId}/organizers/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Update one organizer permissions.
 *
 * @param {number|string} eventId Event identifier
 * @param {string} username Organizer username
 * @param {Object} payload Permission payload
 * @returns {Promise<Object>} organizer payload
 */
export async function updateEventOrganizer(eventId, username, payload) {
  return jsonRequest(`/me/events/${eventId}/organizers/${encodeURIComponent(username)}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/**
 * Remove one organizer from one event.
 *
 * @param {number|string} eventId Event identifier
 * @param {string} username Organizer username
 * @returns {Promise<void>} empty response on success
 */
export async function removeEventOrganizer(eventId, username) {
  return jsonRequest(`/me/events/${eventId}/organizers/${encodeURIComponent(username)}/`, {
    method: 'DELETE',
  });
}

/**
 * List profiles excluded from one owned wish item.
 *
 * @param {number|string} itemId Wish item identifier
 * @returns {Promise<Array>} excluded profile list
 */
export async function listWishItemExcludedProfiles(itemId) {
  const data = await jsonRequest(`/me/wishes/${itemId}/excluded-profiles/`, {
    method: 'GET',
  });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Exclude one profile from one owned wish item.
 *
 * @param {number|string} itemId Wish item identifier
 * @param {string} username Username to exclude
 * @returns {Promise<Object>} created profile payload
 */
export async function addWishItemExcludedProfile(itemId, username) {
  return jsonRequest(`/me/wishes/${itemId}/excluded-profiles/`, {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
}

/**
 * Remove one wish item exclusion.
 *
 * @param {number|string} itemId Wish item identifier
 * @param {string} username Username to remove
 * @returns {Promise<void>} empty response on success
 */
export async function removeWishItemExcludedProfile(itemId, username) {
  return jsonRequest(`/me/wishes/${itemId}/excluded-profiles/${encodeURIComponent(username)}/`, {
    method: 'DELETE',
  });
}

/**
 * Delete an event for the current user.
 *
 * @param {number|string} eventId The event identifier
 * @returns {Promise<void>} empty response on success
 */
export async function deleteEvent(eventId) {
  return jsonRequest(`/me/events/${eventId}/`, {
    method: 'DELETE',
  });
}

export async function register({ firstName, lastName, email, password, confirmPassword }) {
  const payload = {
    email,
    first_name: firstName,
    last_name: lastName,
    password,
    password_confirm: confirmPassword,
  };
  return jsonRequest('/auth/register/', {
    method: 'POST',
    body: JSON.stringify(payload),
    skipAuth: true,
  });
}

export async function login({ email, password, remember = true }) {
  const payload = {
    email,
    password,
    remember_me: remember,
  };
  const data = await jsonRequest('/auth/login/', {
    method: 'POST',
    body: JSON.stringify(payload),
    // We use the default credentials from jsonRequest
    skipAuth: true,
  });
  // Persist the access token using the remember flag.  The backend
  // returns an object with ``access_token`` and ``expires_in``
  if (data && data.access_token) {
    setAccessToken(data.access_token, remember);
  }
  return data;
}

export async function logout() {
  // Logout uses cookies, so we must include credentials.
  // Clear any stored tokens on the client side before calling logout.
  clearAccessToken();
  clearStoredLastVisitedRoute();
  await fetch(`${BASE_URL}/auth/csrf/`, {
    method: 'GET',
    credentials: 'include',
  });
  const csrfToken = readCsrfTokenFromCookies();
  const headers = {};
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }
  return jsonRequest('/auth/logout/', {
    method: 'POST',
    credentials: 'include',
    headers,
  });
}

/**
 * Submit a bug report on behalf of the authenticated user.
 *
 * @param {Object} payload The bug report data
 * @returns {Promise<Object>} the response payload
 */
export async function reportBug(payload) {
  const body = {
    title: payload.title,
    description: payload.description,
    steps_to_reproduce: payload.stepsToReproduce,
    expected_behavior: payload.expectedBehavior,
    actual_behavior: payload.actualBehavior,
    severity: payload.severity,
    page_url: payload.pageUrl,
  };
  return jsonRequest('/support/bug-report/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function resendVerification(email) {
  return jsonRequest('/auth/resend-verification/', {
    method: 'POST',
    body: JSON.stringify({ email }),
    skipAuth: true,
  });
}

/**
 * Check whether an email address has already been verified.
 *
 * @param {string} email The email address to check
 * @returns {Promise<{email: string, is_verified: boolean}>} status payload
 */
export async function checkEmailVerificationStatus(email) {
  const query = new URLSearchParams({ email }).toString();
  return jsonRequest(`/auth/verify-email/status/?${query}`, {
    method: 'GET',
    skipAuth: true,
  });
}

export async function verifyEmailToken(token) {
  return jsonRequest(`/auth/verify-email/${token}/`, {
    method: 'GET',
    skipAuth: true,
  });
}

/**
 * Request a password reset email.
 *
 * @param {string} email The email address to send the reset link to
 * @returns {Promise<Object>} success response
 */
export async function requestResetPassword(email) {
  return jsonRequest('/auth/reset-password/', {
    method: 'POST',
    body: JSON.stringify({ email }),
    skipAuth: true,
  });
}

/**
 * Submit a new password for a given reset token.
 *
 * @param {string} token The password reset token
 * @param {Object} param1 New password and confirmation
 * @returns {Promise<Object>} success response
 */
export async function resetPassword(token, { password, confirmPassword }) {
  return jsonRequest('/auth/reset-password/confirm/', {
    method: 'POST',
    body: JSON.stringify({
      token,
      password,
      password_confirm: confirmPassword,
    }),
    skipAuth: true,
  });
}

export async function updateProfile(profileData) {
  return jsonRequest('/me/profile/', {
    method: 'PATCH',
    body: JSON.stringify(profileData),
  });
}

/**
 * Convert an absolute feed pagination URL into an API path.
 *
 * @param {string} pageUrl Absolute or relative pagination URL
 * @returns {string} API path that can be used with jsonRequest
 */
function normalizeApiPath(pageUrl) {
  if (!pageUrl) return '/me/feed/';
  if (pageUrl.startsWith('/')) {
    if (pageUrl.startsWith('/api/')) {
      return pageUrl.replace(/^\/api/, '') || '/';
    }
    return pageUrl;
  }
  try {
    const parsedUrl = new URL(pageUrl);
    const pathWithQuery = `${parsedUrl.pathname}${parsedUrl.search || ''}`;
    if (pathWithQuery.startsWith('/api/')) {
      return pathWithQuery.replace(/^\/api/, '') || '/';
    }
    return pathWithQuery;
  } catch (error) {
    return '/me/feed/';
  }
}

/**
 * Retrieve a paginated page of the canonical feed.
 *
 * @param {Object} params Feed query parameters
 * @param {string} params.pageUrl Optional absolute or relative next page URL
 * @returns {Promise<Object>} paginated feed payload
 */
export async function listMyFeed(params = {}) {
  const path = params.pageUrl ? normalizeApiPath(params.pageUrl) : '/me/feed/';
  return jsonRequest(path, {
    method: 'GET',
  });
}

/**
 * Send a feed exposure analytics event when an item appears.
 *
 * @param {Object} payload Exposure payload
 * @returns {Promise<Object>} created exposure row
 */
export async function createFeedExposure(payload) {
  return jsonRequest('/me/feed/exposures/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Send a feed engagement analytics event for ranking signals.
 *
 * @param {Object} payload Engagement payload
 * @returns {Promise<Object>} created engagement row
 */
export async function createFeedEngagement(payload) {
  return jsonRequest('/me/feed/engagements/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Change the current user's password using the existing password for confirmation.
 *
 * @param {Object} param0 Password payload
 * @returns {Promise<Object>} success response
 */
export async function changePassword({ oldPassword, newPassword, newPasswordConfirm }) {
  return jsonRequest('/me/password/', {
    method: 'POST',
    body: JSON.stringify({
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    }),
  });
}

/**
 * Request an email address change with verification.
 *
 * @param {Object} param0 Email payload
 * @returns {Promise<Object>} success response
 */
export async function changeEmail({ newEmail, newEmailConfirm, currentPassword }) {
  return jsonRequest('/me/email/', {
    method: 'POST',
    body: JSON.stringify({
      new_email: newEmail,
      new_email_confirm: newEmailConfirm,
      current_password: currentPassword,
    }),
  });
}

export async function createWishList({ name, description, visibility }) {
  return jsonRequest('/me/wishlists/', {
    method: 'POST',
    body: JSON.stringify({ name, description, visibility }),
  });
}

/**
 * Update an existing wish list owned by the current user.
 *
 * @param {Object} data The fields to update
 * @returns {Promise<Object>} updated wish list data
 */
export async function updateWishList(data) {
  return jsonRequest('/me/wishlist/', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Delete an existing wish list owned by the current user.
 *
 * @returns {Promise<void>} empty response on success
 */
export async function deleteWishList() {
  return jsonRequest('/me/wishlist/', {
    method: 'DELETE',
  });
}

/**
 * Retrieve the default visible wish list for a specific user.
 *
 * @param {string} username The username of the wish list owner
 * @returns {Promise<Object>} wish list data
 */
export async function getUserWishList(username) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/u/${encodedUsername}/wishlist/`, {
    method: 'GET',
  });
}

/**
 * Retrieve one paginated page of another user's default visible wishes.
 *
 * @param {string} username The wish list owner username
 * @param {Object} params Query params for pagination and search
 * @param {number|string} params.page Page number
 * @param {string} params.q Search query
 * @returns {Promise<Object>} paginated wish list items payload
 */
export async function listUserWishListItems(username, params = {}) {
  const encodedUsername = encodeURIComponent(username);
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page);
  if (params.q) query.set('q', params.q);
  const queryString = query.toString();
  return jsonRequest(`/u/${encodedUsername}/wishes/${queryString ? `?${queryString}` : ''}`, {
    method: 'GET',
  });
}

/**
 * Retrieve one public wish list item.
 *
 * @param {string} username The wish list owner username
 * @param {number|string} itemId The wish list item identifier
 * @returns {Promise<Object>} public wish list item payload
 */
export async function getUserWishListItem(username, itemId) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/u/${encodedUsername}/wishes/${itemId}/`, {
    method: 'GET',
  });
}

/**
 * Create a new wish under the current user's default wish list.
 * The payload can be JSON or FormData when uploading an image.
 *
 * @param {Object|FormData} itemData The item payload
 * @returns {Promise<Object>} the newly created item data
 */
export async function createWishItem(itemData) {
  const isMultipart = itemData instanceof FormData;
  return jsonRequest('/me/wishes/', {
    method: 'POST',
    body: isMultipart ? itemData : JSON.stringify(itemData),
  });
}

/**
 * Retrieve a wish list item owned by the current user.
 *
 * @param {number|string} itemId The item identifier
 * @returns {Promise<Object>} the wish list item data
 */
export async function getWishListItem(itemId) {
  return jsonRequest(`/me/wishes/${itemId}/`, {
    method: 'GET',
  });
}

/**
 * Update a wish list item owned by the current user.
 * The payload can be JSON or FormData when uploading an image.
 *
 * @param {number|string} itemId The item identifier
 * @param {Object|FormData} itemData The updated item fields
 * @returns {Promise<Object>} updated item data
 */
export async function updateWishItem(itemId, itemData) {
  const isMultipart = itemData instanceof FormData;
  return jsonRequest(`/me/wishes/${itemId}/`, {
    method: 'PATCH',
    body: isMultipart ? itemData : JSON.stringify(itemData),
  });
}

/**
 * Delete a wish list item owned by the current user.
 *
 * @param {number|string} itemId The item identifier
 * @returns {Promise<void>} empty response on success
 */
export async function deleteWishItem(itemId) {
  return jsonRequest(`/me/wishes/${itemId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve comments for a wish list.
 *
 * @returns {Promise<Array>} array of comment objects
 */
export async function listWishListComments() {
  const data = await jsonRequest('/me/wishlist/comments/', {
    method: 'GET',
  });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create a comment on a wish list.
 *
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} created comment data
 */
export async function createWishListComment(payload) {
  return jsonRequest('/me/wishlist/comments/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Update one wish list comment.
 *
 * @param {number|string} commentId The comment identifier
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} updated comment data
 */
export async function updateWishListComment(commentId, payload) {
  return jsonRequest(`/me/wishlist/comments/${commentId}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete one wish list comment.
 *
 * @param {number|string} commentId The comment identifier
 * @returns {Promise<void>} empty response body
 */
export async function deleteWishListComment(commentId) {
  return jsonRequest(`/me/wishlist/comments/${commentId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve reactions for a wish list.
 *
 * @returns {Promise<Array>} array of reaction objects
 */
export async function listWishListReactions(options = {}) {
  const queryParams = new URLSearchParams();
  if (options.reactionType) {
    queryParams.set('reaction_type', options.reactionType);
  }
  if (options.page != null) {
    queryParams.set('page', String(options.page));
  }
  const queryString = queryParams.toString();
  const path = queryString
    ? `/me/wishlist/reactions/?${queryString}`
    : '/me/wishlist/reactions/';
  const data = await jsonRequest(path, {
    method: 'GET',
  });
  if (options.raw) {
    return data;
  }
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create or update a reaction on a wish list.
 *
 * @param {Object} payload Reaction payload
 * @returns {Promise<Object>} created reaction data
 */
export async function createWishListReaction(payload) {
  return jsonRequest('/me/wishlist/reactions/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete the current user's reaction on a wish list.
 *
 * @param {number|string} reactionId The reaction id to remove
 * @returns {Promise<void>} empty response body
 */
export async function deleteWishListReaction(reactionId) {
  return jsonRequest(`/me/wishlist/reactions/${reactionId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve comments for another user's default visible wish list.
 *
 * @param {string} username The wish list owner username
 * @returns {Promise<Array>} array of comment objects
 */
export async function listUserWishListComments(username) {
  const encodedUsername = encodeURIComponent(username);
  const data = await jsonRequest(`/u/${encodedUsername}/wishlist/comments/`, {
    method: 'GET',
  });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create a comment on another user's default visible wish list.
 *
 * @param {string} username The wish list owner username
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} created comment data
 */
export async function createUserWishListComment(username, payload) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/u/${encodedUsername}/wishlist/comments/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Update one comment on another user's default visible wish list.
 *
 * @param {string} username The wish list owner username
 * @param {number|string} commentId The comment identifier
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} updated comment data
 */
export async function updateUserWishListComment(username, commentId, payload) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/u/${encodedUsername}/wishlist/comments/${commentId}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete one comment on another user's default visible wish list.
 *
 * @param {string} username The wish list owner username
 * @param {number|string} commentId The comment identifier
 * @returns {Promise<void>} empty response body
 */
export async function deleteUserWishListComment(username, commentId) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/u/${encodedUsername}/wishlist/comments/${commentId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve reactions for another user's default visible wish list.
 *
 * @param {string} username The wish list owner username
 * @param {Object} options Query options
 * @returns {Promise<Array|Object>} reaction list or raw paginated payload
 */
export async function listUserWishListReactions(username, options = {}) {
  const encodedUsername = encodeURIComponent(username);
  const queryParams = new URLSearchParams();
  if (options.reactionType) {
    queryParams.set('reaction_type', options.reactionType);
  }
  if (options.page != null) {
    queryParams.set('page', String(options.page));
  }
  const queryString = queryParams.toString();
  const path = queryString
    ? `/u/${encodedUsername}/wishlist/reactions/?${queryString}`
    : `/u/${encodedUsername}/wishlist/reactions/`;
  const data = await jsonRequest(path, {
    method: 'GET',
  });
  if (options.raw) {
    return data;
  }
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create or update a reaction on another user's default visible wish list.
 *
 * @param {string} username The wish list owner username
 * @param {Object} payload Reaction payload
 * @returns {Promise<Object>} created reaction data
 */
export async function createUserWishListReaction(username, payload) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/u/${encodedUsername}/wishlist/reactions/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete the current user's reaction on another user's default visible wish list.
 *
 * @param {string} username The wish list owner username
 * @param {number|string} reactionId The reaction id to remove
 * @returns {Promise<void>} empty response body
 */
export async function deleteUserWishListReaction(username, reactionId) {
  const encodedUsername = encodeURIComponent(username);
  return jsonRequest(`/u/${encodedUsername}/wishlist/reactions/${reactionId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve comments for a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @returns {Promise<Array>} array of comment objects
 */
export async function listWishListItemComments(itemId) {
  const data = await jsonRequest(`/me/wishes/${itemId}/comments/`, {
    method: 'GET',
  });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create a comment on a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} created comment data
 */
export async function createWishListItemComment(itemId, payload) {
  return jsonRequest(`/me/wishes/${itemId}/comments/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Update one wish list item comment.
 *
 * @param {number|string} itemId The item identifier
 * @param {number|string} commentId The comment identifier
 * @param {Object} payload Comment payload
 * @returns {Promise<Object>} updated comment data
 */
export async function updateWishListItemComment(itemId, commentId, payload) {
  return jsonRequest(`/me/wishes/${itemId}/comments/${commentId}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete one wish list item comment.
 *
 * @param {number|string} itemId The item identifier
 * @param {number|string} commentId The comment identifier
 * @returns {Promise<void>} empty response body
 */
export async function deleteWishListItemComment(itemId, commentId) {
  return jsonRequest(`/me/wishes/${itemId}/comments/${commentId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve reactions for a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @param {Object} options Query options
 * @returns {Promise<Array|Object>} reaction list or raw paginated payload
 */
export async function listWishListItemReactions(itemId, options = {}) {
  const queryParams = new URLSearchParams();
  if (options.reactionType) {
    queryParams.set('reaction_type', options.reactionType);
  }
  if (options.page != null) {
    queryParams.set('page', String(options.page));
  }
  const queryString = queryParams.toString();
  const path = queryString
    ? `/me/wishes/${itemId}/reactions/?${queryString}`
    : `/me/wishes/${itemId}/reactions/`;
  const data = await jsonRequest(path, {
    method: 'GET',
  });
  if (options.raw) {
    return data;
  }
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create or update a reaction on a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @param {Object} payload Reaction payload
 * @returns {Promise<Object>} created reaction data
 */
export async function createWishListItemReaction(itemId, payload) {
  return jsonRequest(`/me/wishes/${itemId}/reactions/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete the current user reaction on a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @param {number|string} reactionId The reaction identifier
 * @returns {Promise<void>} empty response body
 */
export async function deleteWishListItemReaction(itemId, reactionId) {
  return jsonRequest(`/me/wishes/${itemId}/reactions/${reactionId}/`, {
    method: 'DELETE',
  });
}

/**
 * Retrieve reservations for a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @returns {Promise<Array>} array of reservation objects
 */
export async function listWishListItemReservations(itemId) {
  const data = await jsonRequest(`/me/wishes/${itemId}/reservations/`, {
    method: 'GET',
  });
  return Array.isArray(data.results) ? data.results : data;
}

/**
 * Create a reservation for a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @param {Object} payload Reservation payload
 * @returns {Promise<Object>} created reservation data
 */
export async function createWishListItemReservation(itemId, payload) {
  return jsonRequest(`/me/wishes/${itemId}/reservations/`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Update a reservation for a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @param {number|string} reservationId The reservation identifier
 * @param {Object} payload Reservation payload
 * @returns {Promise<Object>} updated reservation data
 */
export async function updateWishListItemReservation(itemId, reservationId, payload) {
  return jsonRequest(`/me/wishes/${itemId}/reservations/${reservationId}/`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/**
 * Delete a reservation for a wish list item.
 *
 * @param {number|string} itemId The item identifier
 * @param {number|string} reservationId The reservation identifier
 * @returns {Promise<void>} empty response on success
 */
export async function deleteWishListItemReservation(itemId, reservationId) {
  return jsonRequest(`/me/wishes/${itemId}/reservations/${reservationId}/`, {
    method: 'DELETE',
  });
}

// Export session helpers so that tests and other modules can inspect auth state.
export {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  getRememberMePreference,
  isSessionInvalidError,
  restoreAccessSession,
};

/**
 * Fetch onboarding status for the current user.
 *
 * The response includes the last completed step, next step, and a
 * completion flag.  The frontend maps step numbers to routes.
 *
 * @returns {Promise<Object>} onboarding status payload
 */
export async function fetchOnboardingStatus() {
  return jsonRequest('/me/onboarding/status/', { method: 'GET' });
}

/**
 * Mark an onboarding step as completed for the current user.
 *
 * @param {number} stepId The onboarding step number that is complete
 * @returns {Promise<Object>} updated onboarding status payload
 */
export async function completeOnboardingStep(stepId) {
  return jsonRequest('/me/onboarding/steps/complete/', {
    method: 'POST',
    body: JSON.stringify({ step_id: stepId }),
  });
}

export async function searchUsers(queryOrParams, { country, city, page } = {}) {
  const params = new URLSearchParams();
  if (typeof queryOrParams === 'object' && queryOrParams !== null) {
    if (queryOrParams.q) params.set('q', queryOrParams.q);
    if (queryOrParams.country) params.set('country', queryOrParams.country);
    if (queryOrParams.city) params.set('city', queryOrParams.city);
    if (queryOrParams.page) params.set('page', queryOrParams.page);
  } else if (queryOrParams) {
    params.set('q', queryOrParams);
  }
  if (country) params.set('country', country);
  if (city) params.set('city', city);
  if (page) params.set('page', page);
  return jsonRequest(`/search/users/?${params.toString()}`, {
    method: 'GET',
  });
}

/**
 * Perform a global search across people, wish lists, items, stories, and events.
 *
 * @param {string} query Search query
 * @param {Object} options Optional filters for the request
 * @param {string[]} options.types Types to include in the response
 * @returns {Promise<Object>} paginated search results
 */
export async function searchGlobal(query, { types, filters } = {}) {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (Array.isArray(types) && types.length > 0) {
    params.set('types', types.join(','));
  }
  if (filters && typeof filters === 'object') {
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      params.set(key, value);
    });
  }
  return jsonRequest(`/search/?${params.toString()}`, {
    method: 'GET',
  });
}

export async function inviteByEmail(email) {
  return jsonRequest('/me/friends/invite/', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Fetch the current user's profile.  Returns a JSON object with the
 * profile fields or throws if the request is unauthorized or fails.
 */
export async function fetchProfile() {
  return jsonRequest('/me/profile/', {
    method: 'GET',
  });
}

/**
 * Request a new access token using the refresh token cookie.  This
 * helper will first ensure that a CSRF cookie is set by calling the
 * CSRF endpoint, then POST to the refresh endpoint.  On success it
 * returns the JSON body containing the new access token and expiry.
 */
export async function refreshAccessToken() {
  await fetch(`${BASE_URL}/auth/csrf/`, {
    method: 'GET',
    credentials: 'include',
  });
  const csrfToken = readCsrfTokenFromCookies();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (csrfToken) {
    headers['X-CSRFToken'] = csrfToken;
  }
  const res = await fetch(`${BASE_URL}/auth/refresh/`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw buildApiError(data, res.status);
  }
  return res.json();
}
